import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { ColorBlock, SectionHeading, BodyText, BLOCKS, P, PD, A } from './BlogShared.jsx';

/* ─── Glassmorphic Devices: Laptop + Tablet + Phone, bottom-aligned, black bezels ─── */
const DevicesSVG = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) el.classList.add('dev-active'); },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <Box ref={ref} sx={{ width: '100%', maxWidth: 560, mx: 'auto' }}>
      <style>{`
        .dev-el { opacity: 0; transform: translateY(18px); transition: all 0.7s cubic-bezier(.16,1,.3,1); }
        .dev-active .dev-el { opacity: 1; transform: translateY(0); }
        .dev-active .dev-el:nth-of-type(1) { transition-delay: 0.1s; }
        .dev-active .dev-el:nth-of-type(2) { transition-delay: 0.3s; }
        .dev-active .dev-el:nth-of-type(3) { transition-delay: 0.5s; }
      `}</style>
      <svg viewBox="0 0 560 300" fill="none" style={{ width: '100%', display: 'block' }}>
        <defs>
          {/* Soft background gradient */}
          <linearGradient id="devBg" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={`${P}08`} />
            <stop offset="50%" stopColor="#F8F6FF" />
            <stop offset="100%" stopColor={`${A}05`} />
          </linearGradient>
          {/* Screen content gradient */}
          <linearGradient id="devScreen" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FAFAFE" />
            <stop offset="100%" stopColor="#F3F0FA" />
          </linearGradient>
          {/* Glass card fill */}
          <linearGradient id="devGlass" x1="0" y1="0" x2="0.5" y2="1">
            <stop offset="0%" stopColor="white" stopOpacity="0.7" />
            <stop offset="100%" stopColor="white" stopOpacity="0.3" />
          </linearGradient>
          {/* Accent orb */}
          <linearGradient id="devOrb" x1="0.2" y1="0" x2="0.8" y2="1">
            <stop offset="0%" stopColor={P} />
            <stop offset="100%" stopColor={A} />
          </linearGradient>
          <filter id="devShadow">
            <feDropShadow dx="0" dy="4" stdDeviation="8" floodColor="#1a1a2e" floodOpacity="0.10" />
          </filter>
        </defs>

        {/* ═══ Shared soft background ═══ */}
        <rect x="10" y="10" width="540" height="270" rx="24" fill="url(#devBg)" />

        {/* ═══ PHONE (left, bottom-aligned) ═══ */}
        <g className="dev-el" filter="url(#devShadow)">
          {/* Bezel */}
          <rect x="40" y="120" width="75" height="140" rx="14" fill="#1C1C1E" />
          {/* Screen */}
          <rect x="47" y="132" width="61" height="115" rx="3" fill="url(#devScreen)" />
          {/* Dynamic island */}
          <rect x="61" y="124" width="30" height="5" rx="2.5" fill="#2C2C2E" />
          {/* Screen content */}
          <rect x="53" y="141" width="40" height="6" rx="3" fill={`${P}15`} />
          <rect x="53" y="153" width="49" height="20" rx="5" fill="url(#devGlass)" stroke={`${P}10`} strokeWidth="0.5" />
          <rect x="57" y="158" width="22" height="3.5" rx="1.5" fill={`${P}18`} />
          <rect x="57" y="164" width="34" height="3" rx="1.5" fill={`${P}08`} />
          <rect x="53" y="179" width="49" height="20" rx="5" fill="url(#devGlass)" stroke={`${A}08`} strokeWidth="0.5" />
          <rect x="57" y="184" width="20" height="3.5" rx="1.5" fill={`${A}14`} />
          <rect x="57" y="190" width="30" height="3" rx="1.5" fill={`${A}06`} />
          {/* Button */}
          <rect x="53" y="206" width="34" height="13" rx="6.5" fill="url(#devOrb)" opacity="0.55" />
          {/* Home indicator */}
          <rect x="63" y="251" width="26" height="3" rx="1.5" fill="#3A3A3C" />
        </g>

        {/* ═══ LAPTOP — screen only (center, largest) ═══ */}
        <g className="dev-el" filter="url(#devShadow)">
          {/* Bezel */}
          <rect x="145" y="70" width="240" height="160" rx="10" fill="#1C1C1E" />
          {/* Screen */}
          <rect x="153" y="78" width="224" height="143" rx="4" fill="url(#devScreen)" />
          {/* Screen content — dashboard */}
          <rect x="164" y="90" width="80" height="8" rx="4" fill={`${P}18`} />
          <rect x="164" y="106" width="95" height="40" rx="8" fill="url(#devGlass)" stroke={`${P}10`} strokeWidth="0.8" />
          <rect x="170" y="112" width="40" height="5" rx="2.5" fill={`${P}22`} />
          <rect x="170" y="122" width="60" height="4" rx="2" fill={`${P}10`} />
          <circle cx="190" cy="136" r="5" fill={`${P}12`} />
          <rect x="268" y="106" width="95" height="40" rx="8" fill="url(#devGlass)" stroke={`${A}08`} strokeWidth="0.8" />
          <rect x="274" y="112" width="35" height="5" rx="2.5" fill={`${A}18`} />
          <rect x="274" y="122" width="55" height="4" rx="2" fill={`${A}08`} />
          {/* Chart line */}
          <path d="M168 170 L200 162 L232 165 L264 155 L296 148 L328 142 L360 137" stroke="url(#devOrb)" strokeWidth="1.8" strokeLinecap="round" opacity="0.5" />
          {/* Bottom bar */}
          <rect x="164" y="185" width="200" height="26" rx="6" fill="url(#devGlass)" stroke={`${P}08`} strokeWidth="0.5" />
          <rect x="172" y="192" width="50" height="5" rx="2.5" fill={`${P}12`} />
          <rect x="172" y="200" width="80" height="4" rx="2" fill={`${P}08`} />
          {/* Chin webcam dot */}
          <circle cx="265" cy="225" r="2" fill="#2C2C2E" />
        </g>

        {/* ═══ TABLET (right, bottom-aligned) ═══ */}
        <g className="dev-el" filter="url(#devShadow)">
          {/* Bezel */}
          <rect x="415" y="95" width="110" height="165" rx="12" fill="#1C1C1E" />
          {/* Screen */}
          <rect x="423" y="108" width="94" height="139" rx="3" fill="url(#devScreen)" />
          {/* Camera */}
          <circle cx="470" cy="102" r="2.5" fill="#2C2C2E" />
          {/* Screen content — list view */}
          <rect x="431" y="116" width="60" height="7" rx="3.5" fill={`${P}15`} />
          <rect x="431" y="130" width="78" height="22" rx="6" fill="url(#devGlass)" stroke={`${P}10`} strokeWidth="0.6" />
          <rect x="437" y="136" width="30" height="4" rx="2" fill={`${P}18`} />
          <rect x="437" y="144" width="50" height="3" rx="1.5" fill={`${P}08`} />
          <rect x="431" y="158" width="78" height="22" rx="6" fill="url(#devGlass)" stroke={`${P}10`} strokeWidth="0.6" />
          <rect x="437" y="164" width="35" height="4" rx="2" fill={`${A}15`} />
          <rect x="437" y="172" width="45" height="3" rx="1.5" fill={`${A}06`} />
          <rect x="431" y="186" width="78" height="22" rx="6" fill="url(#devGlass)" stroke={`${P}10`} strokeWidth="0.6" />
          <rect x="437" y="192" width="28" height="4" rx="2" fill={`${P}14`} />
          <rect x="437" y="200" width="48" height="3" rx="1.5" fill={`${P}06`} />
          {/* CTA */}
          <rect x="431" y="215" width="40" height="14" rx="7" fill="url(#devOrb)" opacity="0.6" />
          {/* Home indicator */}
          <rect x="455" y="251" width="30" height="3" rx="1.5" fill="#3A3A3C" />
        </g>
      </svg>
    </Box>
  );
};

/* ─── Device SVG icons ─── */
const DeviceIcon = ({ children }) => (
  <svg viewBox="0 0 32 32" width="28" height="28" fill="none">{children}</svg>
);

const IconLaptop = () => (
  <DeviceIcon>
    <rect x="5" y="6" width="22" height="15" rx="2" stroke={P} strokeWidth="1.8" fill={`${P}08`} />
    <rect x="7" y="8" width="18" height="11" rx="1" fill={`${P}15`} />
    <path d="M3 21h26" stroke={P} strokeWidth="2" strokeLinecap="round" />
  </DeviceIcon>
);

const IconMobile = () => (
  <DeviceIcon>
    <rect x="10" y="4" width="12" height="24" rx="3" stroke={A} strokeWidth="1.8" fill={`${A}08`} />
    <rect x="12" y="7" width="8" height="15" rx="1" fill={`${A}12`} />
    <circle cx="16" cy="25" r="1.5" fill={A} opacity="0.5" />
  </DeviceIcon>
);

const IconPOS = () => (
  <DeviceIcon>
    <rect x="8" y="3" width="16" height="22" rx="2.5" stroke={PD} strokeWidth="1.8" fill={`${PD}08`} />
    <rect x="10" y="5" width="12" height="10" rx="1" fill={`${PD}12`} />
    <rect x="10" y="17" width="4" height="3" rx="0.8" fill={`${PD}20`} />
    <rect x="15.5" y="17" width="4" height="3" rx="0.8" fill={`${PD}20`} />
    <path d="M12 28h8" stroke={PD} strokeWidth="1.5" strokeLinecap="round" />
  </DeviceIcon>
);

const IconWorkstation = () => (
  <DeviceIcon>
    <rect x="6" y="4" width="20" height="16" rx="2" stroke={P} strokeWidth="1.8" fill={`${P}08`} />
    <rect x="8" y="6" width="16" height="12" rx="1" fill={`${P}12`} />
    <path d="M14 20v4M18 20v4M11 24h10" stroke={P} strokeWidth="1.5" strokeLinecap="round" />
  </DeviceIcon>
);

const IconTablet = () => (
  <DeviceIcon>
    <rect x="7" y="4" width="18" height="24" rx="3" stroke={A} strokeWidth="1.8" fill={`${A}08`} />
    <rect x="9" y="7" width="14" height="17" rx="1.5" fill={`${A}12`} />
    <circle cx="16" cy="27" r="1.2" fill={A} opacity="0.5" />
  </DeviceIcon>
);

const IconMore = () => (
  <DeviceIcon>
    <circle cx="10" cy="16" r="2.5" fill={P} opacity="0.5" />
    <circle cx="16" cy="16" r="2.5" fill={P} opacity="0.7" />
    <circle cx="22" cy="16" r="2.5" fill={P} opacity="0.5" />
  </DeviceIcon>
);

/* ─── Device cards data ─── */
const deviceCards = [
  { Icon: IconLaptop, label: 'Laptop' },
  { Icon: IconMobile, label: 'Mobile' },
  { Icon: IconPOS, label: 'POS Device' },
  { Icon: IconWorkstation, label: 'Workstation' },
  { Icon: IconTablet, label: 'Tablet' },
  { Icon: IconMore, label: 'More' },
];

export const AllDevicesSection = () => (
  <ColorBlock bg={BLOCKS.lavender} innerBg="white" innerRadius="32px">
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: 'center',
        gap: { xs: 5, md: 8 },
      }}
    >
      {/* Text content */}
      <Box sx={{ flex: '1 1 45%', minWidth: 0 }}>
        <SectionHeading size="lg" sx={{ color: PD, mb: 2 }}>
          <Box component="span" sx={{ fontFamily: "'Instrument Serif', serif", fontWeight: 700, fontStyle: 'italic' }}>One</Box> platform.
          <br />
          <Box
            component="span"
            sx={{ fontFamily: "'Instrument Serif', serif", fontWeight: 700, fontStyle: 'italic', background: `linear-gradient(90deg, ${P}, ${A})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}
          >
            Every device.
          </Box>
        </SectionHeading>
        <BodyText sx={{ mb: 4 }}>
          One platform, every device. Always connected, always in control.
        </BodyText>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5 }}>
          {deviceCards.map((d) => (
            <Box
              key={d.label}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.8,
                p: { xs: 1.5, md: 2 },
                borderRadius: '16px',
                background: 'rgba(255,255,255,0.55)',
                backdropFilter: 'blur(16px)',
                border: '1px solid rgba(255,255,255,0.6)',
                boxShadow: '0 2px 12px rgba(69,39,160,0.05)',
                transition: 'all 0.25s ease',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 6px 20px ${P}12` },
              }}
            >
              <d.Icon />
              <Typography sx={{ fontSize: { xs: '0.68rem', md: '0.8rem' }, fontWeight: 600, color: PD, textAlign: 'center' }}>
                {d.label}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Sync badge */}
        <Box
          sx={{
            display: 'inline-flex', alignItems: 'center', gap: 1,
            mt: 4, px: 2.5, py: 1,
            borderRadius: '50px',
            background: `${P}06`,
            border: `1px solid ${P}10`,
          }}
        >
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: P }} />
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: P }}>
            Real-time sync across all devices
          </Typography>
        </Box>
      </Box>

      {/* Device SVG */}
      <Box sx={{ flex: '1 1 55%', minWidth: 0 }}>
        <DevicesSVG />
      </Box>
    </Box>
  </ColorBlock>
);
